import { type ReactNode, useEffect } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { ToastProvider } from '../components/toast';
import { useAuth } from '../lib/auth';
import { registrarExpiracaoSessao } from '../lib/http';
import { BASE_PATH } from './lib/basePath';
import { Botao } from './componentes/ui';
import { HomePagina } from './rotas/Home';
import { LandingConvite } from './rotas/LandingConvite';
import { MeusVouchers } from './rotas/MeusVouchers';
import { MinhasIndicacoes } from './rotas/MinhasIndicacoes';
import { PortalLogin } from './rotas/PortalLogin';
import { PortalPrimeiroAcesso } from './rotas/PortalPrimeiroAcesso';
import { PortalRecuperar } from './rotas/PortalRecuperar';
import { Termos } from './rotas/Termos';

/**
 * Portal do cliente + landing do convite (UX-PORTAL-CLIENTE §0). Roteamento raiz
 * por papel: cliente → Home; admin/atendente → painel; não logado → login do
 * portal. O painel tem o seu próprio `App`/router.
 */
function TelaCarregando(): ReactNode {
  return (
    <div className="tipo-nota flex min-h-screen items-center justify-center bg-fundo text-tinta-2">
      Carregando…
    </div>
  );
}

/**
 * Sessão de equipe (admin/atendente) aberta no portal do cliente — acontece
 * quando alguém da equipe loga aqui por engano, ou testando (`lib/session.ts`
 * usa uma única chave de refresh para painel e portal; um papel só por pessoa
 * é aceitável, não é este passe que resolve a arquitetura de sessão). Em vez
 * de um `location.replace` mudo para o painel, uma tela breve e explícita —
 * a pessoa decide, não é redirecionada sem entender por quê.
 */
function TelaOutroPapel(): ReactNode {
  return (
    <div className="flex min-h-screen items-center bg-fundo px-5">
      <div className="mx-auto flex w-full max-w-[480px] flex-col items-start gap-4">
        <p className="tipo-corpo text-tinta">Você está logado como equipe.</p>
        <Botao variante="primario" onClick={() => window.location.assign(`${BASE_PATH}/painel`)}>
          Ir para o painel
        </Botao>
      </div>
    </div>
  );
}

function RaizPorPapel(): ReactNode {
  const { usuario, carregando } = useAuth();

  if (carregando) return <TelaCarregando />;
  if (!usuario) return <PortalLogin />;
  if (usuario.role !== 'cliente') return <TelaOutroPapel />;
  return <HomePagina />;
}

/** Rota do portal que exige cliente logado. Sem sessão → volta para `/` (login). */
function ExigeCliente({ children }: { children: ReactNode }): ReactNode {
  const { usuario, carregando } = useAuth();
  if (carregando) return <TelaCarregando />;
  if (!usuario || usuario.role !== 'cliente') return <Navigate to="/" replace />;
  return <>{children}</>;
}

export function PortalApp(): ReactNode {
  const navigate = useNavigate();
  const { encerrarLocal } = useAuth();

  useEffect(() => {
    registrarExpiracaoSessao(() => {
      encerrarLocal();
      navigate('/', { replace: true });
    });
  }, [navigate, encerrarLocal]);

  return (
    <ToastProvider>
      <Routes>
        <Route path="/" element={<RaizPorPapel />} />
        <Route path="/primeiro-acesso" element={<PortalPrimeiroAcesso />} />
        <Route path="/recuperar" element={<PortalRecuperar />} />
        <Route path="/termos" element={<Termos />} />
        <Route path="/convite" element={<LandingConvite />} />
        <Route
          path="/vouchers"
          element={
            <ExigeCliente>
              <MeusVouchers />
            </ExigeCliente>
          }
        />
        <Route
          path="/indicacoes"
          element={
            <ExigeCliente>
              <MinhasIndicacoes />
            </ExigeCliente>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ToastProvider>
  );
}
